import { v4 as uuidV4 } from 'uuid';
import { CriteriaConstraintField, CriteriaConstraintItem, RankFilters } from '../api/decisions.api';
import { AppCriteria, AppCriteriaField, CriteriaConstraint } from '../types/decisions';
import { AppThemeColors } from '../types/common';

type EnvironmentVariableName =
    'REACT_APP_API_URL' |
    'REACT_APP_PLATFORM_URL' |
    'REACT_APP_IMAGE_BASE_URL' |
    'REACT_APP_VIRTUAL_AGENT_KEYS' |
    'REACT_APP_VIRTUAL_AGENT_SERVER_URL' |
    'REACT_APP_AWS_REGION' |
    'REACT_APP_AWS_TRANSCRIBE_IDENTITY_POOL_ID' |
    'REACT_APP_LEGACY_APP_URL';

export const GUEST_INFO_STORAGE_KEY = 'guest_info';

export type GuestInfo = {
    name: string;
};

export const scrollToAnchor = (hash: string) => {
    const element = document.getElementById(hash);
    element && element.scrollIntoView({ behavior: 'smooth' });
};

const customEnvs: {
    [key: string]: string
} = {};

// Override for Dang2 and for local
const paxdiWhitelistedDomains = [
    'https://paxdi.com',
    'https://dev.paxdi.com',
    'https://dev.platform.paxdi.com',
    'https://preview.form.sparkdit.com',
    'https://preview.testing.sparkdit.com',
    // 'http://localhost:3000',
];

if (paxdiWhitelistedDomains.includes(window.location.origin)) {
    const platformURL = process.env['REACT_APP_PLATFORM_URL'] || 'https://dashboard.paxdi.com';
    customEnvs['REACT_APP_IMAGE_BASE_URL'] = platformURL;
    customEnvs['REACT_APP_API_URL'] = platformURL;
    customEnvs['REACT_APP_PLATFORM_URL'] = platformURL;
}

export const getEnvironmentVariable = (name: EnvironmentVariableName): string => {
    return customEnvs[name] || process.env[name] || '';
};

export const hasLocalStorageValue = (key: string): boolean => {
    const value = localStorage.getItem(key);

    return Boolean(
        value &&
        value !== 'null' &&
        value !== 'undefined',
    );
};

export const getGuestInfo = (): GuestInfo | undefined => {
    const storedGuestInfo = localStorage.getItem(GUEST_INFO_STORAGE_KEY);

    if (!storedGuestInfo) {
        return undefined;
    }

    try {
        const parsedGuestInfo = JSON.parse(storedGuestInfo) as GuestInfo;

        if (parsedGuestInfo?.name) {
            return {
                name: parsedGuestInfo.name,
            };
        }
    } catch {
        return {
            name: storedGuestInfo,
        };
    }
};

export const getRandomItem = (reactions: string[]): string => {
    return reactions[Math.floor(Math.random() * reactions.length)];
};

const getFieldValueByType = (field: any, options: {
    isZipSingleField: boolean;
}) => {
    switch (field.type) {
        case 'number':
            if (Array.isArray(field.val)) {
                return [
                    {
                        name: 0,
                        value: field.val[0],
                    },
                    {
                        name: 1,
                        value: field.val[1],
                    },
                ];
            }

            return field.val;
        case 'bool':
            return {
                name: field.name,
                value: field.val,
            };
        case 'select':
        case 'multiselect':
            return Object.keys(field.val).map(key => (
                {
                    name: key,
                    // @ts-ignore
                    value: +field.val[key],
                }
            ));
        case 'color':
            return convertColorsArrayToObjectsArray(field.val);
        case 'zipcode':
            const items: string[] = options.isZipSingleField ? field.val.slice(0, 1) : field.val;
            return items.map((item, idx) => (
                {
                    name: idx,
                    value: item,
                }
            ));
        case 'geoloc':
            return [
                {
                    name: 'longitude',
                    value: field.val.longitude,
                },
                {
                    name: 'latitude',
                    value: field.val.latitude,
                },
            ];
        default:
            return field.val;
    }
};

const convertColorsArrayToObjectsArray = (array: Array<{
    color: string;
    isSelected: boolean;
}>): {
    name: string;
    value: string;
}[] => {
    return array.filter(({ isSelected }) => isSelected).map((item, index) => {
        return {
            name: index + '',
            value: item.color,
        };
    });
};

const constructConstraintRules = (items: CriteriaConstraint[]): CriteriaConstraintField | CriteriaConstraintItem => {
    const item = items.shift() as CriteriaConstraint;
    if (items.length < 1) {
        return {
            rule: item.rule,
            value: item.value,
        };
    }

    return {
        operator: items[0].operator,
        left: {
            rule: item.rule,
            value: item.value,
        },
        right: constructConstraintRules(items),
    };
};

export const transformCriteriaData = (
    criteria: AppCriteria,
    constraints: {
        [key: string]: CriteriaConstraint[];
    },
): RankFilters => {
    let transformedConstraints: RankFilters['constraints'] | null = {};
    const keys = Object.keys(constraints);

    if (keys.length < 1) {
        transformedConstraints = null;
    } else {
        keys.forEach(key => {
            if (transformedConstraints && constraints[key].length > 0) {
                transformedConstraints[key] = constructConstraintRules(
                    constraints[key],
                ) as CriteriaConstraintItem;
            }
        });
    }

    const filteredCriteria = getCriteriaFieldsToCalculate(criteria.fields)
    .filter(item => {
        return (
            item.val !== null
            && item.val !== undefined
            && item.val !== ''
            && item.name !== 'Name'
        );
    })
    .map(field => {
        const isZipSingleField = field.viewConfigs.view === 'SingleField';

        return {
            id: field.id,
            name: field.name as string,
            importance: +field.importance,
            value: getFieldValueByType(
                field,
                {
                    isZipSingleField: isZipSingleField,
                },
            ),
            type: field.type,
        };
    });

    return {
        id: criteria.id,
        criteria: filteredCriteria,
        constraints: transformedConstraints,
        dataset: null,
    };
};

export const getFieldName = (
    data?: string | {
        value: string;
    },
): string => {
    if (data === undefined) {
        return '-';
    }

    if (typeof data === 'string') {
        return data;
    }

    return data.value || '-';
};

export const getCriteriaNameValuePairs = (
    data: any,
): {
    name: string;
    value: string;
} => {
    const res = {
        name: data.name,
        value: '',
    };

    if (data.hasOwnProperty('value')) {
        res.value = data.value;
    } else {
        const keys = Object.keys(data.val);

        if (keys.length) {
            res.value = data.val[keys[0]];
        }
    }

    return res;
};

export const getCriteriaValueByType = (
    field: AppCriteriaField,
    newValue: any,
    key: string = 'initialKey',
) => {
    switch (field.type) {
        case 'text':
        case 'dynamic':
        case 'number':
        case 'date':
        case 'bool':
            return newValue;
        case 'select':
        case 'multiselect':
        case 'geoloc':
            return {
                ...(
                    field.val as Object
                ),
                [key]: newValue,
            };
        case 'zipcode':
            const fieldValue = [ ...(
                (
                    field.val as unknown as string[] || []
                )
            ) ];

            fieldValue[key as unknown as number] = newValue;

            return fieldValue;
        default:
            return newValue;
    }
};

export const transformCriteriaObjectToArray = (
    data: {},
    icons: string[] = [],
    link: string = '',
): {
    name: string;
    value: number;
    icon: string;
    link: string;
} [] => {
    const result: {
        name: string;
        value: number;
        icon: string;
        link: string;
    }[] = [];

    const keys = Object.keys(data);

    keys.forEach((key) => {
        result.push({
            name: key,
            // @ts-ignore
            value: data[key],
            // @ts-ignore
            icon: icons[key],
            // @ts-ignore
            link,
        });
    });

    return result;
};

export const generateUniqueId = (): string => {
    return uuidV4();
};

export const filterCriteriaFields = (
    rawFields: AppCriteriaField[],
): {
    fields: AppCriteriaField[],
    technoFields: AppCriteriaField[],
} => {
    const fields: AppCriteriaField[] = [];
    const technoFields: AppCriteriaField[] = [];

    rawFields.forEach((item) => {
        if (item.technicalField) {
            technoFields.push(item);
        } else {
            fields.push(item);
        }
    });

    return {
        fields,
        technoFields,
    };
};

export const getCriteriaFieldsToDisplayInResults = (rawFields: AppCriteriaField[]): AppCriteriaField[] => {
    return rawFields.filter((item) => item.show_in_result);
};

export const getCriteriaFieldsToCalculate = (rawFields: AppCriteriaField[]): AppCriteriaField[] => {
    return rawFields.filter((item) => item.calculate);
};

export const getCriteriaFieldsToDisplayInCriteria = (rawFields: AppCriteriaField[]): AppCriteriaField[] => {
    return rawFields.filter((item) => !item.hide_importance);
};

export const getCookie = (name: string): string => {
    const match = document.cookie.match(
        new RegExp('(^|;\\s*)' + name + '=([^;]*)'),
    );

    return match ? decodeURIComponent(match[2]) : '';
};

export const formatNumber = (value: number) => new Intl.NumberFormat().format(value);

export const applyAppTheme = (colors: AppThemeColors) => {
    const root = document.documentElement;

    root.style.setProperty(
        '--app-primary',
        colors.primary,
    );

    root.style.setProperty(
        '--app-secondary',
        colors.secondary,
    );

    root.style.setProperty(
        '--app-background',
        colors.background,
    );
};